import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { parseRM } from '@/features/billing/money';
import { api } from '@/lib/api';
import { useStockFail } from './stock-fail';
import { dayLabel } from '@/pages/ops/register-format';
import local from './stock.module.css';

/** GET /stock/counts/:id (expected and difference only after Compare or finishing). */
interface CountDetail {
  id: string;
  kind: string;
  status: string;
  startedAt: string;
  lines: {
    id: string;
    medicine: string;
    controlled: boolean;
    batchNo: string;
    expiry: string;
    counted: number | null;
    expected: number | null;
    difference: number | null;
    reason: string | null;
  }[];
}

/**
 * RL-1649 (gap #27): count each batch on the shelf blind (the expected
 * quantity stays hidden), then Compare to see the differences, give a reason
 * for each and Finish. Stock is adjusted when the count finishes.
 */
export function CountDialog({
  id,
  canManage,
  onClose,
}: {
  id: string;
  canManage: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const [compare, setCompare] = useState(false);
  const q = useQuery({
    queryKey: ['stock', 'count', id, compare],
    queryFn: ({ signal }) =>
      api
        .get<CountDetail>(`/stock/counts/${id}`, {
          signal,
          query: compare ? { compare: 'true' } : {},
        })
        .then((r) => r.data),
  });
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (finish: boolean) =>
      api.post(`/stock/counts/${id}`, {
        finish,
        lines: (q.data?.lines ?? [])
          .map((l) => {
            const raw = counted[l.id];
            const value =
              raw === undefined ? l.counted : raw.trim() ? Number(raw) : null;
            const reason = (reasons[l.id] ?? l.reason ?? '').trim();
            return value === null
              ? null
              : {
                  lineId: l.id,
                  counted: value,
                  ...(reason ? { reason } : {}),
                };
          })
          .filter((x) => x !== null),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  const open = q.data?.status === 'open' && canManage;
  // Counts are fixed once compared, so the expected figure cannot steer them.
  const editable = open && !compare;
  const countsValid = () => {
    const bad = Object.values(counted).some(
      (v) => v.trim() && (!Number.isInteger(Number(v)) || Number(v) < 0),
    );
    if (bad) toast.show({ tone: 'error', title: 'Counts are whole numbers' });
    return !bad;
  };
  const run = (finish: boolean) => {
    if (!countsValid()) return;
    void save
      .mutateAsync(finish)
      .then(() => {
        toast.show({
          tone: 'success',
          title: finish ? 'Count finished; stock adjusted' : 'Count saved',
        });
        if (finish) onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={q.data ? `Count · ${dayLabel(q.data.startedAt)}` : 'Count'}
      footer={
        open ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button loading={save.isPending} onClick={() => run(false)}>
              Save
            </Button>
            {!compare ? (
              <Button
                onClick={() => {
                  if (!countsValid()) return;
                  void save
                    .mutateAsync(false)
                    .then(() => setCompare(true))
                    .catch(fail);
                }}
              >
                Compare
              </Button>
            ) : (
              <Button
                variant="primary"
                loading={save.isPending}
                onClick={() => run(true)}
              >
                Finish count
              </Button>
            )}
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <p className={local.cardNote}>
        {compare || q.data?.status !== 'open'
          ? 'Give a reason for every difference before finishing.'
          : 'Count what is on the shelf. The expected quantity shows after Compare.'}
      </p>
      <DataTable
        caption="Count lines"
        rows={q.data?.lines}
        loading={q.isLoading}
        error={q.error}
        rowKey={(l) => l.id}
        empty={<EmptyState title="Nothing to count" />}
        columns={[
          {
            key: 'm',
            header: 'Medicine',
            render: (l) => (
              <>
                <strong>{l.medicine}</strong>
                {l.controlled && <Badge tone="warning">Controlled</Badge>}
              </>
            ),
          },
          {
            key: 'b',
            header: 'Batch',
            render: (l) => (
              <span className="mono">{`${l.batchNo} · ${l.expiry}`}</span>
            ),
          },
          {
            key: 'c',
            header: 'Counted',
            align: 'right',
            render: (l) =>
              editable ? (
                <Input
                  aria-label={`Counted · ${l.medicine} ${l.batchNo}`}
                  inputMode="numeric"
                  value={
                    counted[l.id] ??
                    (l.counted === null ? '' : String(l.counted))
                  }
                  onChange={(e) =>
                    setCounted({ ...counted, [l.id]: e.target.value })
                  }
                />
              ) : (
                (l.counted ?? '-')
              ),
          },
          ...(compare || q.data?.status !== 'open'
            ? [
                {
                  key: 'e',
                  header: 'Expected',
                  align: 'right' as const,
                  render: (l: CountDetail['lines'][number]) =>
                    l.expected ?? '-',
                },
                {
                  key: 'd',
                  header: 'Difference',
                  align: 'right' as const,
                  render: (l: CountDetail['lines'][number]) =>
                    l.difference ? (
                      <Badge tone="warning">{l.difference}</Badge>
                    ) : (
                      (l.difference ?? '-')
                    ),
                },
                {
                  key: 'r',
                  header: 'Reason',
                  render: (l: CountDetail['lines'][number]) =>
                    open && l.difference ? (
                      <Input
                        aria-label={`Reason · ${l.medicine} ${l.batchNo}`}
                        value={reasons[l.id] ?? l.reason ?? ''}
                        maxLength={200}
                        onChange={(e) =>
                          setReasons({ ...reasons, [l.id]: e.target.value })
                        }
                      />
                    ) : (
                      (l.reason ?? '-')
                    ),
                },
              ]
            : []),
        ]}
      />
    </Dialog>
  );
}

/** GET /stock/medicines/:id (the part the transfer window needs). */
interface MedicineDetail {
  batches: {
    id: string;
    batchNo: string;
    expiry: string;
    qtyOnHand: number;
    status: string;
  }[];
}

/**
 * RL-1651 (gap #27): send stock to another branch, batch by batch. The other
 * branch receives it from its own Transfers tab.
 */
export function TransferDialog({
  medicines,
  fromBranchId,
  onClose,
}: {
  medicines: { id: string; name: string }[];
  fromBranchId: string;
  onClose: () => void;
}) {
  // Any active branch can receive, also one the sender does not work at.
  const allBranches = useQuery({
    queryKey: ['branches', 'active'],
    queryFn: ({ signal }) =>
      api
        .get<{ id: string; name: string }[]>('/branches', { signal })
        .then((r) => r.data),
  });
  const branches = (allBranches.data ?? []).filter(
    (b) => b.id !== fromBranchId,
  );
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const [toBranchId, setTo] = useState('');
  const [medicineId, setMedicine] = useState('');
  const [batchId, setBatch] = useState('');
  const [qty, setQty] = useState('');
  const [lines, setLines] = useState<
    { batchId: string; qty: number; label: string }[]
  >([]);
  const detail = useQuery({
    queryKey: ['stock', 'medicine', medicineId],
    queryFn: ({ signal }) =>
      api
        .get<MedicineDetail>(`/stock/medicines/${medicineId}`, { signal })
        .then((r) => r.data),
    enabled: !!medicineId,
  });
  const batches = (detail.data?.batches ?? []).filter(
    (b) => b.qtyOnHand > 0 && b.status === 'available',
  );
  const send = useMutation({
    mutationFn: () =>
      api.post('/stock/transfers', {
        toBranchId,
        lines: lines.map(({ batchId: id, qty: n }) => ({
          batchId: id,
          qty: n,
        })),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  const addLine = () => {
    const batch = batches.find((b) => b.id === batchId);
    const n = Number(qty);
    if (!batch || !Number.isInteger(n) || n < 1 || n > batch.qtyOnHand)
      return toast.show({
        tone: 'error',
        title: `Choose a batch and a quantity up to ${batch?.qtyOnHand ?? 'what is on hand'}`,
      });
    const name = medicines.find((m) => m.id === medicineId)?.name ?? '';
    setLines([
      ...lines.filter((l) => l.batchId !== batch.id),
      { batchId: batch.id, qty: n, label: `${name} · ${batch.batchNo} × ${n}` },
    ]);
    setBatch('');
    setQty('');
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title="Send transfer"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={send.isPending}
            onClick={() => {
              if (!toBranchId || !lines.length)
                return toast.show({
                  tone: 'error',
                  title: 'Choose the branch and add at least one batch',
                });
              void send
                .mutateAsync()
                .then(() => {
                  toast.show({ tone: 'success', title: 'Transfer sent' });
                  onClose();
                })
                .catch(fail);
            }}
          >
            Send
          </Button>
        </>
      }
    >
      <div className={local.drawerForm}>
        <Field label="To branch">
          <Select
            value={toBranchId}
            placeholder="Choose…"
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
        <Field label="Medicine">
          <Select
            value={medicineId}
            placeholder="Choose…"
            options={medicines.map((m) => ({ value: m.id, label: m.name }))}
            onChange={(e) => {
              setMedicine(e.target.value);
              setBatch('');
            }}
          />
        </Field>
        <div className={local.twoCol}>
          <Field label="Batch">
            <Select
              value={batchId}
              placeholder={detail.isLoading ? 'Loading…' : 'Choose…'}
              options={batches.map((b) => ({
                value: b.id,
                label: `${b.batchNo} · exp ${b.expiry} · ${b.qtyOnHand} on hand`,
              }))}
              onChange={(e) => setBatch(e.target.value)}
            />
          </Field>
          <Field label="Quantity to send">
            <Input
              inputMode="numeric"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </Field>
        </div>
        <Button size="sm" onClick={addLine}>
          Add to transfer
        </Button>
        {lines.length > 0 && (
          <ul aria-label="Transfer lines">
            {lines.map((l) => (
              <li key={l.batchId}>{l.label}</li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

/**
 * RL-1651 (gap #27): write off an expired (or unusable) batch, or return it
 * to the supplier, with a reason and a second person as witness; a
 * psychotropic needs the Drug Enforcement Officer present.
 */
export function DisposeDialog({
  batch,
  witnesses,
  onClose,
}: {
  batch: {
    id: string;
    medicine: string;
    batchNo: string;
    qty: number;
    psychotropic: boolean;
  };
  witnesses: { id: string; fullName: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const [action, setAction] = useState<'write_off' | 'return_supplier'>(
    'write_off',
  );
  const [qty, setQty] = useState(String(batch.qty));
  const [reason, setReason] = useState('');
  const [witnessId, setWitness] = useState('');
  const [deo, setDeo] = useState(false);
  // Only destroying a psychotropic needs the officer; a return does not.
  const needsDeo = batch.psychotropic && action === 'write_off';
  const dispose = useMutation({
    mutationFn: () =>
      api.post(`/stock/batches/${batch.id}/dispose`, {
        action,
        qty: Number(qty),
        reason: reason.trim(),
        witnessId,
        ...(needsDeo ? { deoPresent: deo } : {}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  const submit = () => {
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1 || n > batch.qty)
      return toast.show({
        tone: 'error',
        title: `Quantity: 1 to ${batch.qty}`,
      });
    if (reason.trim().length < 3 || !witnessId)
      return toast.show({
        tone: 'error',
        title: 'Give the reason and the witness',
      });
    if (needsDeo && !deo)
      return toast.show({
        tone: 'error',
        title: 'A psychotropic needs the Drug Enforcement Officer present',
      });
    void dispose
      .mutateAsync()
      .then(() => {
        toast.show({
          tone: 'success',
          title:
            action === 'write_off' ? 'Written off' : 'Returned to supplier',
        });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Remove from stock · ${batch.medicine} ${batch.batchNo}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={dispose.isPending} onClick={submit}>
            {action === 'write_off' ? 'Write off' : 'Return to supplier'}
          </Button>
        </>
      }
    >
      <div className={local.drawerForm}>
        <ChipToggle
          label="What happens"
          single
          options={[
            { value: 'write_off', label: 'Write off' },
            { value: 'return_supplier', label: 'Return to supplier' },
          ]}
          selected={[action]}
          onChange={(v) => v[0] && setAction(v[0])}
        />
        <Field label="Quantity">
          <Input
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Field>
        <Field label="Reason">
          <Input
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Field label="Witness (second person)">
          <Select
            value={witnessId}
            placeholder="Choose…"
            options={witnesses.map((w) => ({ value: w.id, label: w.fullName }))}
            onChange={(e) => setWitness(e.target.value)}
          />
        </Field>
        {needsDeo && (
          <ChipToggle
            label="Drug Enforcement Officer present"
            single
            options={[
              { value: 'yes', label: 'Present' },
              { value: 'no', label: 'Not present' },
            ]}
            selected={[deo ? 'yes' : 'no']}
            onChange={(v) => setDeo(v[0] === 'yes')}
          />
        )}
      </div>
    </Dialog>
  );
}

/** RL-1652 (gap #27): a medicine's selling price per unit and per pack. */
export function PriceDialog({
  medicine,
  onClose,
}: {
  medicine: {
    id: string;
    name: string;
    unit: string;
    unitPriceSen: number | null;
    packSize: number;
    packPriceSen: number | null;
  };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const rm = (sen: number | null) =>
    sen === null ? '' : (sen / 100).toFixed(2);
  const [unit, setUnit] = useState(rm(medicine.unitPriceSen));
  const [pack, setPack] = useState(rm(medicine.packPriceSen));
  const [packSize, setPackSize] = useState(String(medicine.packSize));
  const save = useMutation({
    mutationFn: (body: object) =>
      api.post(`/stock/prices/${medicine.id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  const submit = () => {
    const unitSen = parseRM(unit);
    const packSen = pack.trim() ? parseRM(pack) : null;
    const size = Number(packSize);
    if (unitSen === null || (pack.trim() && packSen === null))
      return toast.show({ tone: 'error', title: 'Check the prices' });
    if (!Number.isInteger(size) || size < 1)
      return toast.show({
        tone: 'error',
        title: 'Pack size is a whole number',
      });
    void save
      .mutateAsync({
        unitPriceSen: unitSen,
        packPriceSen: packSen,
        packSize: size,
      })
      .then(() => {
        toast.show({ tone: 'success', title: 'Price saved' });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Price · ${medicine.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className={local.drawerForm}>
        <Field label={`Price per ${medicine.unit} (RM)`}>
          <Input
            inputMode="decimal"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
        </Field>
        <div className={local.twoCol}>
          <Field label="Pack size">
            <Input
              inputMode="numeric"
              value={packSize}
              onChange={(e) => setPackSize(e.target.value)}
            />
          </Field>
          <Field label="Pack price (RM, optional)">
            <Input
              inputMode="decimal"
              value={pack}
              onChange={(e) => setPack(e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * RL-1653 (gap #28): when to reorder a medicine at this branch: reorder
 * point, par level (what a reorder fills up to) and its supplier.
 */
export function ReorderSettingsDialog({
  medicine,
  suppliers,
  onClose,
}: {
  medicine: {
    id: string;
    name: string;
    unit: string;
    reorderPoint: number;
    parLevel: number;
    supplier: { id: string } | null;
  };
  suppliers: { id: string; name: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const [reorder, setReorder] = useState(String(medicine.reorderPoint));
  const [par, setPar] = useState(String(medicine.parLevel));
  const [supplierId, setSupplier] = useState(medicine.supplier?.id ?? '');
  const save = useMutation({
    mutationFn: (body: object) => api.post('/stock/settings', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  const submit = () => {
    const r = Number(reorder);
    const p = Number(par);
    if (![r, p].every((n) => Number.isInteger(n) && n >= 0))
      return toast.show({ tone: 'error', title: 'Use whole numbers' });
    if (p < r)
      return toast.show({
        tone: 'error',
        title: 'Par level is at least the reorder point',
      });
    void save
      .mutateAsync({
        medicines: [
          {
            medicineId: medicine.id,
            reorderPoint: r,
            parLevel: p,
            supplierId: supplierId || null,
          },
        ],
      })
      .then(() => {
        toast.show({ tone: 'success', title: 'Reorder settings saved' });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Reorder · ${medicine.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className={local.drawerForm}>
        <div className={local.twoCol}>
          <Field label={`Reorder at (${medicine.unit})`}>
            <Input
              inputMode="numeric"
              value={reorder}
              onChange={(e) => setReorder(e.target.value)}
            />
          </Field>
          <Field label={`Par level (${medicine.unit})`}>
            <Input
              inputMode="numeric"
              value={par}
              onChange={(e) => setPar(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Supplier">
          <Select
            value={supplierId}
            placeholder="No supplier"
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(e) => setSupplier(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/** RL-1653: reject a draft purchase order with a reason (API #226). */
export function RejectOrderDialog({
  order,
  onClose,
}: {
  order: { id: string; number: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const [reason, setReason] = useState('');
  const reject = useMutation({
    mutationFn: () =>
      api.post(`/stock/orders/${order.id}/reject`, { reason: reason.trim() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Reject · ${order.number}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            loading={reject.isPending}
            onClick={() => {
              if (reason.trim().length < 3)
                return toast.show({ tone: 'error', title: 'Give the reason' });
              void reject
                .mutateAsync()
                .then(() => {
                  toast.show({ tone: 'success', title: 'Order rejected' });
                  onClose();
                })
                .catch(fail);
            }}
          >
            Reject
          </Button>
        </>
      }
    >
      <Field label="Reason">
        <Input
          value={reason}
          maxLength={300}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}
